/**
 * 落下する料理オブジェクト。
 *
 * 合体判定に必要な状態（id / level / isMerging / createdAt / bodyId）を保持する。
 *
 * 【重要】物理ボディと見た目のスプライトを分離している。
 * Phaser の Matter.Image は scale を変えるとボディ自体まで拡縮されるため、
 * 「テクスチャを縮小して表示する」だけで当たり判定が壊れてしまう。
 * そのため、
 *   - 当たり判定 : matter.add.circle / fromVertices で作った純粋な Matter ボディ
 *                  （実画像に輪郭 hull があればその多角形、無ければ半径 radius の円）
 *   - 見た目     : 物理と無関係な通常の Image（毎フレーム位置・角度を同期）
 * という構成にし、演出でスプライトを拡縮しても物理挙動が変化しないようにしている。
 *
 * 見た目の弾力（プルプル）もこの分離のおかげで自由に付けられる。
 * 着地・衝突の強さに応じて「潰れ → 伸び → 収まる」減衰振動を
 * スプライトのスケールだけに適用しているため、物理挙動は一切変わらない。
 */

import Phaser from 'phaser';

import { GAME_TUNING } from '../config/gameConfig';
import type { StageItemDefinition } from '../config/types';
import type { OverLineCandidate } from '../systems/GameOverSystem';
import type { MergeCandidate } from '../systems/MergeSystem';
import type { ResolvedShape } from '../utils/itemShape';

/**
 * Matter 本体の Sleeping モジュール。
 * Phaser は Matter をそのまま同梱しているが型定義には現れないため、ここで解決しておく。
 */
const MATTER_SLEEPING = (
  Phaser.Physics.Matter as unknown as { Matter: { Sleeping: MatterJS.SleepingFactory } }
).Matter.Sleeping;

export interface FoodPieceOptions {
  scene: Phaser.Scene;
  item: StageItemDefinition;
  textureKey: string;
  id: number;
  x: number;
  y: number;
  createdAt: number;
  /** 物理ボディと表示の形（resolveItemShape の結果）。 */
  shape: ResolvedShape;
  /** 指定すると「あつあつ」の湯気を立てる（高レベル料理向け）。 */
  steamTextureKey?: string;
}

export class FoodPiece implements MergeCandidate, OverLineCandidate {
  readonly id: number;
  readonly item: StageItemDefinition;
  readonly level: number;
  readonly radius: number;
  readonly createdAt: number;
  /** 当たり判定に使う Matter ボディ（面積 = π × shape.radius²）。 */
  readonly body: MatterJS.BodyType;
  /** 表示用スプライト（物理には影響しない）。 */
  readonly sprite: Phaser.GameObjects.Image;
  readonly bodyId: number;
  readonly shape: ResolvedShape;

  isMerging = false;

  private readonly scene: Phaser.Scene;
  /** 湯気。料理が転がっても回転させないので、常に真上へ立ち上る。 */
  private readonly steam: Phaser.GameObjects.Image | null = null;
  private destroyed = false;

  /** setDisplaySize 直後のスケール。以降の演出はこれに倍率を掛ける。 */
  private readonly baseScaleX: number;
  private readonly baseScaleY: number;
  /** 合体直後のポップ倍率（1 が等倍）。 */
  private popScale = 1;
  /** 弾力振動の振幅・位相・角速度。 */
  private wobbleAmp = 0;
  private wobblePhase = 0;
  private readonly wobbleOmega: number;
  private readonly idlePhase: number;
  private readonly idleAmp: number;
  /** 直前フレームの落下速度（着地の強さを測るために保持）。 */
  private lastSpeed = 0;

  constructor(options: FoodPieceOptions) {
    const { scene, item, textureKey, id, x, y, createdAt, shape } = options;
    const physics = GAME_TUNING.physics;
    const jiggle = GAME_TUNING.jiggle;

    this.scene = scene;
    this.id = id;
    this.item = item;
    this.level = item.level;
    // 盤面で縮めている料理（config/shapeScales.ts）は、その分だけ小さい半径として扱う
    this.radius = shape.radius;
    this.createdAt = createdAt;
    this.shape = shape;

    // density から質量を決めることで、setMass を使わずに狙った質量にできる
    const bodyOptions = {
      label: `food:${item.id}`,
      restitution: physics.restitution,
      friction: physics.friction,
      frictionStatic: physics.frictionStatic,
      frictionAir: physics.frictionAir,
      sleepThreshold: physics.sleepThreshold,
      density: item.mass / shape.area,
    };

    this.body = (
      shape.kind === 'polygon'
        ? scene.matter.add.fromVertices(x, y, shape.vertices, bodyOptions)
        : scene.matter.add.circle(x, y, shape.radius, bodyOptions)
    ) as MatterJS.BodyType;
    this.bodyId = this.body.id;

    this.sprite = scene.add
      .image(x, y, textureKey)
      .setOrigin(shape.originX, shape.originY)
      .setDisplaySize(shape.displaySize, shape.displaySize)
      .setDepth(10 + item.level);
    this.sprite.setData('pieceId', id);
    this.baseScaleX = this.sprite.scaleX;
    this.baseScaleY = this.sprite.scaleY;

    // 大きい料理ほどゆっくり揺れる（重そうに見せる）
    this.wobbleOmega =
      Phaser.Math.Clamp(jiggle.baseFrequencyHz * Math.pow(18 / shape.radius, 0.34), 2.6, 9) *
      Math.PI *
      2;
    this.idlePhase = (id % 17) * 0.37;
    this.idleAmp = jiggle.idleAmplitude;

    if (options.steamTextureKey) {
      this.steam = scene.add
        .image(x, y - shape.radius * 0.78, options.steamTextureKey)
        .setOrigin(0.5, 1)
        .setDisplaySize(shape.radius * 1.7, shape.radius * 2.1)
        .setDepth(9)
        .setAlpha(0);
      scene.tweens.add({
        targets: this.steam,
        // displaySize で決めた scale を壊さないよう、揺らぎは alpha だけで表現する
        alpha: { from: 0.06, to: 0.34 },
        duration: 2100,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }

  get alive(): boolean {
    return !this.destroyed;
  }

  get x(): number {
    return this.body.position.x;
  }

  get y(): number {
    return this.body.position.y;
  }

  /** 表示スプライトを物理ボディへ追従させる（毎フレーム呼ぶ）。 */
  sync(deltaMs: number): void {
    if (this.destroyed) return;

    this.detectLanding();
    this.advanceWobble(deltaMs);

    const { x: bx, y: by } = this.body.position;
    this.sprite.setPosition(bx, by);
    this.sprite.setRotation(this.body.angle);
    this.applyScale();
    // 湯気は回転させない（常に真上へ立ち上らせる）
    this.steam?.setPosition(bx, by - this.radius * 0.78);
  }

  /**
   * 衝突したときに「潰れ」を発生させる。
   * strength は 0〜1 で、大きいほど深く潰れる。
   */
  impact(strength: number): void {
    if (this.destroyed) return;
    const jiggle = GAME_TUNING.jiggle;
    const amp = Phaser.Math.Clamp(strength, 0, 1) * jiggle.maxAmplitude;
    if (amp <= this.wobbleAmp * 0.6) return;
    this.wobbleAmp = Math.min(jiggle.maxAmplitude, amp);
    this.wobblePhase = 0;
  }

  /** 合体直後の「ぷるん」と膨らむ演出。 */
  playPop(): void {
    if (this.destroyed || !this.sprite.active) return;
    const merge = GAME_TUNING.merge;
    const holder = { value: 0.58 };
    this.popScale = holder.value;
    this.scene.tweens.killTweensOf(holder);
    this.scene.tweens.add({
      targets: holder,
      value: merge.popScale,
      duration: merge.popDurationMs,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        this.popScale = holder.value;
      },
      onComplete: () => {
        this.scene.tweens.add({
          targets: holder,
          value: 1,
          duration: merge.popDurationMs * 1.4,
          ease: 'Back.easeOut',
          onUpdate: () => {
            this.popScale = holder.value;
          },
          onComplete: () => {
            this.popScale = 1;
          },
        });
      },
    });
    // 生まれた瞬間にひと揺れ入れて弾力を強調する
    this.wobbleAmp = GAME_TUNING.jiggle.maxAmplitude;
    this.wobblePhase = 0;
  }

  /** 投下時の初速。 */
  setVelocityY(velocityY: number): void {
    if (this.destroyed) return;
    this.scene.matter.body.setVelocity(this.body, { x: this.body.velocity.x, y: velocityY });
  }

  /**
   * スリープを解除する。
   *
   * Matter は衝突でしかスリープを解除しないため、支えていた料理が
   * 合体で「消えた」場合は上の料理が眠ったまま宙に浮いてしまう。
   * 料理が減ったときに明示的に起こすことで、必ず落下を再開させる。
   */
  wake(): void {
    if (this.destroyed || !this.body.isSleeping) return;
    MATTER_SLEEPING.set(this.body, false);
    // 眠っている間に溜まっていた位置補正を捨て、素直に落ち始めさせる
    this.lastSpeed = 0;
  }

  /** すり抜け・暴走防止のため速度を制限する。 */
  clampVelocity(): void {
    if (this.destroyed) return;
    const max = GAME_TUNING.physics.maxVelocity;
    const { x, y } = this.body.velocity;
    const speed = Math.hypot(x, y);
    if (speed > max) {
      const scale = max / speed;
      this.scene.matter.body.setVelocity(this.body, { x: x * scale, y: y * scale });
    }
    this.clampTilt();
    const maxAngular = GAME_TUNING.physics.maxAngularVelocity;
    if (Math.abs(this.body.angularVelocity) > maxAngular) {
      this.scene.matter.body.setAngularVelocity(
        this.body,
        Math.sign(this.body.angularVelocity) * maxAngular,
      );
    }
  }

  /** 丸くない料理は一定以上傾かないようにする（器が逆さまにならない）。 */
  private clampTilt(): void {
    const maxTilt = GAME_TUNING.physics.shapedMaxTiltRad;
    if (this.shape.kind !== 'polygon' || maxTilt === null) return;
    const angle = this.body.angle;
    if (Math.abs(angle) <= maxTilt) return;
    this.scene.matter.body.setAngle(this.body, Math.sign(angle) * maxTilt, false);
    if (Math.sign(this.body.angularVelocity) === Math.sign(angle)) {
      this.scene.matter.body.setAngularVelocity(this.body, 0);
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    // 演出中の Tween が破棄済みスプライトを触らないように必ず止める
    this.scene.tweens.killTweensOf(this.sprite);
    this.scene.matter.world.remove(this.body);
    this.sprite.destroy();
    if (this.steam) {
      this.scene.tweens.killTweensOf(this.steam);
      this.steam.destroy();
    }
  }

  /* -------------------------------- 弾力の実装 -------------------------------- */

  /**
   * 速度が急に落ちたフレームを「着地」とみなして潰れを起こす。
   * collisionstart を取り逃した接触（積み上がった山の沈み込みなど）も拾える。
   */
  private detectLanding(): void {
    const speed = Math.hypot(this.body.velocity.x, this.body.velocity.y);
    const drop = this.lastSpeed - speed;
    this.lastSpeed = speed;
    const jiggle = GAME_TUNING.jiggle;
    if (drop <= jiggle.impactSpeedThreshold) return;
    this.impact((drop - jiggle.impactSpeedThreshold) / jiggle.impactSpeedRange);
  }

  private advanceWobble(deltaMs: number): void {
    const dt = Math.min(0.05, Math.max(0, deltaMs) / 1000);
    this.wobblePhase += this.wobbleOmega * dt;
    this.wobbleAmp *= Math.exp(-dt / GAME_TUNING.jiggle.dampingTauSec);
    if (this.wobbleAmp < 0.0008) this.wobbleAmp = 0;
  }

  /**
   * 体積をおおよそ保ったまま縦横を反転させる（スカッシュ＆ストレッチ）。
   * 常にごくわずかな揺れ（idleAmp）を残すことで、止まっていてもプルプルして見える。
   */
  private applyScale(): void {
    const wobble = this.wobbleAmp * Math.cos(this.wobblePhase);
    const idle = this.idleAmp * Math.sin(this.wobblePhase * 0.42 + this.idlePhase);
    const squash = wobble + idle;
    const sx = this.popScale * (1 + squash);
    const sy = this.popScale * (1 - squash * 0.92);
    this.sprite.setScale(this.baseScaleX * sx, this.baseScaleY * sy);
  }
}
